
import type { ClipName } from "./creaturemesh";

export const GESTURE_LIFETIME_MS = 900;
export const MAX_GESTURES = 24;
export const GESTURE_RISE_PX = 30;
export const GESTURE_START_SCALE = 0.86;
export const GESTURE_END_SCALE = 1.08;

/** One server-confirmed gesture waiting to be painted above its actor. */
export interface GestureMark {
  readonly key: number;
  readonly actorId: number;
  readonly targetId: number;
  readonly emote: number;
  readonly bornMs: number;
}

/** A glyph's time-dependent visual state, kept pure for deterministic tests. */
export interface GestureVisual {
  readonly symbol: string;
  readonly offsetPx: number;
  readonly alpha: number;
  readonly scale: number;
}

/** Progress through the visible part of a gesture, clamped for slow frames. */
export function gesturePhase(mark: GestureMark, nowMs: number): number {
  if (!Number.isFinite(nowMs) || nowMs <= mark.bornMs) return 0;
  return Math.min(1, (nowMs - mark.bornMs) / GESTURE_LIFETIME_MS);
}

/** A stable symbol for each shipped gesture family. Unknown future ids remain visible. */
export function gestureSymbol(emote: number): string {
  switch (emote) {
    case 51: return "♥"; // kiss
    case 52: return "♥♥"; // French kiss
    case 53: return "✋"; // slap
    case 1: return "✦"; // clap
    case 2: return "★"; // congratulate
    case 3: return "☀"; // forgive
    case 4: return "!"; // angry
    case 5: return "♥"; // attractive
    case 6: return "☹"; // sad
    case 7: return "◡"; // shy
    case 8: return "↗"; // cheer up
    case 9: return "…"; // banter
    case 10: return "☺"; // joy
    case 11: return "♪"; // cheer
    case 12: return "♫"; // cheer 2
    case 13:
    case 14:
    case 15:
    case 16:
    case 17:
    case 18:
      return "♪"; // dances
    default:
      return "✦";
  }
}

/** The one-person emotion commands' motions, by the id `emotion.py:31-53`
 *  gives each (`PythonNetworkStreamCommand.cpp:454-530`). */
const SOLO_EMOTE_CLIPS: Readonly<Record<number, ClipName>> = {
  1: "clap", 2: "congratulation", 3: "forgive", 4: "angry", 5: "attractive", 6: "sad",
  7: "shy", 8: "cheerup", 9: "banter", 10: "joy", 11: "cheers1", 12: "cheers2",
  13: "dance1", 14: "dance2", 15: "dance3", 16: "dance4", 17: "dance5", 18: "dance6",
};

const CLASS_SUFFIX = ["Warrior", "Assassin", "Sura", "Shaman"] as const;

/** The motions an emotion plays on its actor and, for the three paired ones, on
 *  its partner. A kiss plays on each side the file for the other side's class
 *  (`InstanceBase.cpp:1831-1843`, a race's class being `race % 4`); a slap pairs
 *  the actor's hurt with the partner's hit (`PythonNetworkStreamCommand.cpp:443-452`). */
export function emoteClips(
  emote: number,
  actorRace: number,
  targetRace: number,
): { readonly actor: ClipName; readonly target: ClipName | null } | null {
  const solo = SOLO_EMOTE_CLIPS[emote];
  if (solo !== undefined) return { actor: solo, target: null };
  const actorClass = CLASS_SUFFIX[((actorRace % 4) + 4) % 4];
  const targetClass = CLASS_SUFFIX[((targetRace % 4) + 4) % 4];
  if (emote === 51) return { actor: `kissWith${targetClass}`, target: `kissWith${actorClass}` };
  if (emote === 52) return { actor: `frenchKissWith${targetClass}`, target: `frenchKissWith${actorClass}` };
  if (emote === 53) return { actor: "slapHurt", target: "slapHit" };
  return null;
}

/** Resolve an event into its visible position and fade without touching state. */
export function gestureVisual(mark: GestureMark, nowMs: number): GestureVisual {
  const phase = gesturePhase(mark, nowMs);
  return {
    symbol: gestureSymbol(mark.emote),
    offsetPx: -GESTURE_RISE_PX * phase,
    alpha: 1 - phase,
    scale: GESTURE_START_SCALE + (GESTURE_END_SCALE - GESTURE_START_SCALE) * phase,
  };
}

/** Paint one human-recognizable glyph. The canvas is deliberately the only side effect. */
export function drawGesture(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  visual: GestureVisual,
  partnered: boolean,
): void {
  if (visual.alpha <= 0 || visual.symbol === "") return;
  ctx.save();
  ctx.globalAlpha = visual.alpha;
  ctx.font = `bold ${Math.round(22 * visual.scale)}px system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = partnered ? "#ff9fc4" : "#ffe08a";
  ctx.shadowColor = "rgba(18, 22, 30, 0.9)";
  ctx.shadowBlur = 4;
  ctx.fillText(visual.symbol, x, y + visual.offsetPx);
  ctx.restore();
}
